import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { getProductVideos } from '../utils/product';

const AudioPlayerContext = createContext();

export const useAudioPlayer = () => {
  return useContext(AudioPlayerContext);
};

export const AudioPlayerProvider = ({ children }) => {
  const [currentPlayback, setCurrentPlayback] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const requestSequence = useRef(0);

  const playVideo = useCallback((product, video, options = {}) => {
    if (!video?.url) return;
    setCurrentPlayback({
      product, video,
      action: options.autoplay === false ? 'select' : 'play',
      requestId: ++requestSequence.current,
    });
    if (!options.preserveExpanded) setIsExpanded(false);
  }, []);

  const playProduct = useCallback((product, options = {}) => {
    const videos = getProductVideos(product);

    if (!videos.length) return;
    playVideo(product, videos[0], options);
  }, [playVideo]);

  const closePlayer = useCallback(() => {
    setCurrentPlayback(null);
    setIsExpanded(false);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const pausePlayback = useCallback(() => {
    const requestId = ++requestSequence.current;
    setCurrentPlayback(prev => prev ? { ...prev, action: 'pause', requestId } : prev);
  }, []);

  const value = useMemo(
    () => ({
      currentPlayback,
      currentProduct: currentPlayback?.product || null,
      currentVideo: currentPlayback?.video || null,
      isExpanded,
      playProduct,
      playVideo,
      closePlayer,
      toggleExpanded,
      pausePlayback,
    }),
    [currentPlayback, isExpanded, playProduct, playVideo, closePlayer, toggleExpanded, pausePlayback]
  );

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
